import { Grid } from '@mui/material';
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import MenuBar, { MenuHeader, MenuSection } from '../../components/new-layout/MenuBar';
import PageLayout from '../../components/new-layout/PageLayout';
import * as routing from '../../constants/Routing';
import { useSearchPlansPredicates } from '../../hooks/plan/useSearchPlansPredicates';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useSearchProjectStopsPredicates } from '../../hooks/stop/useSearchProjectStopsPredicates';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import eventsPanel, { PageViewPages } from '../../services/events';
import { PlanDetails } from './plan-details';
import { PlannerGeoFences } from './planner-geo-fences';
import { PlannerPlans } from './planner-plans';
import { PlannerStops } from './planner-stops';

export const Planner = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { predicates: stopsPredicates, setPredicates: setStopsPredicates } = useSearchProjectStopsPredicates();
  const { predicates: plansPredicates, setPredicates: setPlansPredicates } = useSearchPlansPredicates();

  const planFilterStatus = plansPredicates.find((p) => p.field === `status`);
  const [isMenuBarOpen, setIsMenuBarOpen] = useState<boolean>(true);

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.plans);
  }, []);

  if (!activeProject) {
    return null;
  }

  return (
    <Routes>
      <Route path="/plan/:planId/*" element={<PlanDetails />} />
      <Route
        path="*"
        element={
          <PageLayout style={{ overflow: `hidden` }}>
            <>
              <MenuBar
                onCollapseChange={(collapsed: boolean) => setIsMenuBarOpen(collapsed)}
                content={
                  <div>
                    <MenuHeader>
                      <Grid container direction="row" alignItems="center" justifyContent="space-between">
                        <Text variant="h6" intlId="planner.menubar.title" />
                      </Grid>
                    </MenuHeader>
                    <MenuSection
                      id="planner.menubar.plans"
                      textId="planner-menubar-plans"
                      textIntlId="planner.menubar.plans"
                      textStyle={{ fontWeight: `bold` }}
                      active={location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id)}
                      onClick={() => {
                        setPlansPredicates([...plansPredicates.filter((p) => p.field !== `status`)]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.plans.all"
                      textId="planner-menubar-plans-all"
                      textIntlId="planner.menubar.plans.all"
                      indent={1}
                      active={
                        location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id) &&
                        !planFilterStatus
                      }
                      onClick={() => {
                        setPlansPredicates([...plansPredicates.filter((p) => p.field !== `status`)]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.plans.planning"
                      textId="planner-menubar-plans-planning"
                      textIntlId="planner.menubar.plans.planning"
                      indent={1}
                      active={
                        location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id) &&
                        planFilterStatus?.value === `planning`
                      }
                      onClick={() => {
                        setPlansPredicates([
                          ...plansPredicates.filter((p) => p.field !== `status`),
                          SearchPredicate.create({
                            field: `status`,
                            operator: `eq`,
                            type: `string`,
                            value: `planning`,
                          }),
                        ]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.plans.in_progress"
                      textId="planner-menubar-plans-in_progress"
                      textIntlId="planner.menubar.plans.in_progress"
                      indent={1}
                      active={
                        location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id) &&
                        planFilterStatus?.value === `in_progress`
                      }
                      onClick={() => {
                        setPlansPredicates([
                          ...plansPredicates.filter((p) => p.field !== `status`),
                          SearchPredicate.create({
                            field: `status`,
                            operator: `eq`,
                            type: `string`,
                            value: `in_progress`,
                          }),
                        ]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.plans.finished"
                      textId="planner-menubar-plans-finished"
                      textIntlId="planner.menubar.plans.finished"
                      indent={1}
                      active={
                        location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id) &&
                        planFilterStatus?.value === `finished`
                      }
                      onClick={() => {
                        setPlansPredicates([
                          ...plansPredicates.filter((p) => p.field !== `status`),
                          SearchPredicate.create({
                            field: `status`,
                            operator: `eq`,
                            type: `string`,
                            value: `finished`,
                          }),
                        ]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.plans.draft"
                      textId="planner-menubar-plans-draft"
                      textIntlId="planner.menubar.plans.draft"
                      indent={1}
                      active={
                        location.pathname === routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id) &&
                        planFilterStatus?.value === `draft`
                      }
                      onClick={() => {
                        setPlansPredicates([
                          ...plansPredicates.filter((p) => p.field !== `status`),
                          SearchPredicate.create({
                            field: `status`,
                            operator: `eq`,
                            type: `string`,
                            value: `draft`,
                          }),
                        ]);
                      }}
                    />
                    <MenuSection
                      id="planner.menubar.stops"
                      textId="planner-menubar-stops"
                      textIntlId="planner.menubar.stops"
                      textStyle={{ fontWeight: `bold` }}
                      active={
                        location.pathname === routing.PLANNER.STOPS.LIST.replace(`:projectId`, activeProject!.id) ||
                        location.pathname === routing.PLANNER.STOPS.MAP.replace(`:projectId`, activeProject!.id)
                      }
                      onClick={() => {
                        setStopsPredicates(stopsPredicates);
                      }}
                    />
                    {userCredentials?.subscription.test(`planner.geofences`) &&
                      activeProject.geo_fences_enabled &&
                      userCredentials?.projects.test(activeProject.id, `can_manage_geofences`) && (
                        <MenuSection
                          id="planner.menubar.zones"
                          textId="planner-menubar-zones"
                          textIntlId="planner.menubar.zones"
                          textStyle={{ fontWeight: `bold` }}
                          active={
                            location.pathname === routing.PLANNER.GEO_FENCES.replace(`:projectId`, activeProject!.id)
                          }
                          onClick={() =>
                            navigate(`${routing.PLANNER.GEO_FENCES.replace(`:projectId`, activeProject!.id)}`)
                          }
                        />
                      )}
                  </div>
                }
              />

              <Grid
                container
                direction="column"
                wrap="nowrap"
                style={{
                  zIndex: 1,
                  height: `100%`,
                  width: `calc(100% - ${isMenuBarOpen ? `260px` : `20px`})`,
                  backgroundColor: RoutalPalette.white,
                }}
              >
                <Routes>
                  <Route
                    index
                    element={
                      <Navigate to={routing.PLANNER.PLANS.replace(`:projectId`, projectId ?? activeProject.id)} />
                    }
                  />

                  <Route path="/plans" element={<PlannerPlans />} />

                  <Route path="/stops/*" element={<PlannerStops />} />

                  {userCredentials?.subscription.test(`planner.geofences`) &&
                    activeProject.geo_fences_enabled &&
                    userCredentials?.projects.test(activeProject.id, `can_manage_geofences`) && (
                      <Route path="/geo-fences" element={<PlannerGeoFences project={activeProject} />} />
                    )}

                  <Route
                    path="*"
                    element={
                      <Navigate to={routing.PLANNER.PLANS.replace(`:projectId`, projectId ?? activeProject.id)} />
                    }
                  />
                </Routes>
              </Grid>
            </>
          </PageLayout>
        }
      />
    </Routes>
  );
};
